import { useState, useMemo, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { buttonClassName } from '@/components/ui/buttonStyles'
import { usePageSEO } from '@/hooks/usePageSEO'
import { useTranslation } from 'react-i18next'
import LandingFooter from '@/components/landing/LandingFooter'
import PublicHeader from '@/components/layout/PublicHeader'
import { ToolFAQSection } from '@/components/tools/ToolFAQSection'
import { MoreTools, ToolBreadcrumb } from '@/components/tools/ToolPageParts'
import { TOOL_INPUT, TOOL_LABEL, TOOL_HINT } from '@/components/tools/toolUi'
import useFAQSchema from '@/hooks/useFAQSchema'
import { NICHES, PILLARS, type PillarId } from '@/data/contentIdeas'
import {
    generateCalendar,
    formatDate,
    toISODate,
    toCSV,
    toICS,
    downloadFile,
    type CalendarSlot,
} from '@/utils/contentCalendar'
import {
    ArrowRightIcon,
    ArrowDownTrayIcon,
    CalendarDaysIcon,
    CalendarIcon,
} from '@heroicons/react/24/outline'

const BASE_URL = 'https://www.planwise.social'

const PLATFORM_OPTIONS = ['Instagram', 'TikTok', 'YouTube'] as const
const FREQUENCY_OPTIONS = [3, 5, 7] as const
const WEEKS = 4

function todayISO(): string {
    return toISODate(new Date())
}

export default function ContentCalendarGeneratorPage() {
    const { t, i18n } = useTranslation()
    const pillarLabel = (id: PillarId) => t(PILLARS[id].labelKey)
    const [nicheId, setNicheId] = useState(NICHES[0]!.id)
    const [platforms, setPlatforms] = useState<string[]>(['Instagram'])
    const [postsPerWeek, setPostsPerWeek] = useState<number>(3)
    const [startDate, setStartDate] = useState(todayISO())
    const [seed, setSeed] = useState(1)
    const [generated, setGenerated] = useState(false)
    const resultRef = useRef<HTMLElement>(null)
    const [scrollToResult, setScrollToResult] = useState(false)

    usePageSEO('toolsContentCalendarGenerator', {
        canonicalPath: '/tools/content-calendar-generator',
        ogType: 'website',
        ogImage: `${BASE_URL}/og.jpg`,
        breadcrumbs: [{ name: 'Free Tools', path: '/tools' }, { name: t('tools.pages.contentCalendarGenerator.jsonLdName') }],
        jsonLd: {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'SoftwareApplication',
                    name: t('tools.pages.contentCalendarGenerator.jsonLdName'),
                    applicationCategory: 'BusinessApplication',
                    operatingSystem: 'Web',
                    url: `${BASE_URL}/tools/content-calendar-generator`,
                    description: t('tools.pages.contentCalendarGenerator.jsonLdDescription'),
                    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
                },
                {
                    '@type': 'HowTo',
                    name: t('tools.pages.contentCalendarGenerator.jsonLdName'),
                    description: t('tools.pages.contentCalendarGenerator.jsonLdDescription'),
                    step: [
                        { '@type': 'HowToStep', position: 1, text: 'Choose your content niche.' },
                        { '@type': 'HowToStep', position: 2, text: 'Pick a start date and how many posts per week.' },
                        { '@type': 'HowToStep', position: 3, text: 'Select which platforms you post to.' },
                        { '@type': 'HowToStep', position: 4, text: 'Click Generate to create a ready-to-use content calendar.' },
                    ],
                },
            ],
        },
    })

    const faqs = [
        { question: t('tools.pages.contentCalendarGenerator.faq.q1.question'), answer: t('tools.pages.contentCalendarGenerator.faq.q1.answer') },
        { question: t('tools.pages.contentCalendarGenerator.faq.q2.question'), answer: t('tools.pages.contentCalendarGenerator.faq.q2.answer') },
        { question: t('tools.pages.contentCalendarGenerator.faq.q3.question'), answer: t('tools.pages.contentCalendarGenerator.faq.q3.answer') },
        { question: t('tools.pages.contentCalendarGenerator.faq.q4.question'), answer: t('tools.pages.contentCalendarGenerator.faq.q4.answer') },
    ]
    useFAQSchema({ faqs })

    const niche = useMemo(
        () => NICHES.find((item) => item.id === nicheId) ?? NICHES[0]!,
        [nicheId]
    )

    const slots: CalendarSlot[] = useMemo(() => {
        const [year, month, day] = startDate.split('-').map(Number)
        if (!year || !month || !day) return []
        return generateCalendar({
            niche,
            platforms: platforms.length > 0 ? platforms : ['Instagram'],
            postsPerWeek,
            translateIdea: (textKey: string) => t(textKey, { topik: t(niche.topicKey) }),
            startDate: new Date(year, month - 1, day),
            weeks: WEEKS,
            seed,
        })
    }, [niche, platforms, postsPerWeek, startDate, seed])

    const togglePlatform = (platform: string) => {
        setPlatforms((current) => {
            if (current.includes(platform)) {
                // Never let the last platform be removed — the calendar needs one.
                return current.length === 1 ? current : current.filter((item) => item !== platform)
            }
            return [...current, platform]
        })
    }

    const handleGenerate = () => {
        setSeed((current) => current + 1)
        // Only the first build jumps to the calendar: on a phone it renders below
        // the fold, so without this the button looked like it did nothing.
        if (!generated) setScrollToResult(true)
        setGenerated(true)
    }

    useEffect(() => {
        if (!scrollToResult) return
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setScrollToResult(false)
    }, [scrollToResult])

    const chip = (selected: boolean) =>
        `py-2.5 rounded-xl text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${selected
            ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-700 dark:text-primary-300'
            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-primary-400'
        }`

    const filenameBase = `${t('tools.pages.contentCalendarGenerator.filenameBase')}-${niche.id}-${startDate}`

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
            <div className="flex-grow">
                <PublicHeader />

                {/* Hero */}
                <section className="pt-32 pb-10 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <ToolBreadcrumb path="/tools/content-calendar-generator" />
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            {t('tools.pages.contentCalendarGenerator.heading')}
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-400">
                            {t('tools.pages.contentCalendarGenerator.subtitle')}
                        </p>
                    </div>
                </section>

                {/* Generator */}
                <section className="pb-16 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
                        <div className="grid gap-6 md:grid-cols-2">
                            {/* Niche */}
                            <div className="space-y-2">
                                <label htmlFor="calendar-niche" className={TOOL_LABEL}>
                                    {t('tools.pages.contentCalendarGenerator.niche')}
                                </label>
                                <select
                                    id="calendar-niche"
                                    value={nicheId}
                                    onChange={(e) => setNicheId(e.target.value)}
                                    className={TOOL_INPUT}
                                >
                                    {NICHES.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {t(item.labelKey)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Start date */}
                            <div className="space-y-2">
                                <label htmlFor="calendar-start" className={TOOL_LABEL}>
                                    {t('tools.pages.contentCalendarGenerator.startDate')}
                                </label>
                                <input
                                    id="calendar-start"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className={TOOL_INPUT}
                                />
                            </div>

                            {/* Frequency */}
                            <fieldset className="space-y-2">
                                <legend className={`${TOOL_LABEL} mb-2`}>
                                    {t('tools.pages.contentCalendarGenerator.postsPerWeek')}
                                </legend>
                                <div className="flex gap-2">
                                    {FREQUENCY_OPTIONS.map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            aria-pressed={postsPerWeek === option}
                                            onClick={() => setPostsPerWeek(option)}
                                            className={`flex-1 ${chip(postsPerWeek === option)}`}
                                        >
                                            {option}×
                                        </button>
                                    ))}
                                </div>
                            </fieldset>

                            {/* Platforms */}
                            <fieldset className="space-y-2">
                                <legend className={`${TOOL_LABEL} mb-2`}>
                                    {t('tools.pages.contentCalendarGenerator.platform')}
                                </legend>
                                <div className="flex gap-2">
                                    {PLATFORM_OPTIONS.map((platform) => {
                                        const selected = platforms.includes(platform)
                                        // The last platform cannot be removed; say so instead of ignoring the click.
                                        const locked = selected && platforms.length === 1
                                        return (
                                            <button
                                                key={platform}
                                                type="button"
                                                aria-pressed={selected}
                                                aria-disabled={locked || undefined}
                                                aria-describedby={locked ? 'calendar-platform-hint' : undefined}
                                                onClick={() => togglePlatform(platform)}
                                                className={`flex-1 px-3 ${chip(selected)} ${locked ? 'cursor-default' : ''}`}
                                            >
                                                {platform}
                                            </button>
                                        )
                                    })}
                                </div>
                                <p id="calendar-platform-hint" className={TOOL_HINT}>
                                    {t('tools.pages.contentCalendarGenerator.platformHint')}
                                </p>
                            </fieldset>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <Button onClick={handleGenerate} className="shadow-lg shadow-primary-500/40" leftIcon={<CalendarDaysIcon className="w-5 h-5" />}>
                                {generated ? t('tools.pages.contentCalendarGenerator.regenerate') : t('tools.pages.contentCalendarGenerator.generate')}
                            </Button>
                            {generated && (
                                <>
                                    <Button
                                        variant="outline"
                                        leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
                                        onClick={() =>
                                            downloadFile(
                                                toCSV(slots, pillarLabel),
                                                `${filenameBase}.csv`,
                                                'text/csv;charset=utf-8'
                                            )
                                        }
                                    >
                                        {t('tools.pages.contentCalendarGenerator.downloadCsv')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        leftIcon={<CalendarIcon className="w-5 h-5" />}
                                        onClick={() =>
                                            downloadFile(
                                                toICS(slots, pillarLabel),
                                                `${filenameBase}.ics`,
                                                'text/calendar;charset=utf-8'
                                            )
                                        }
                                    >
                                        {t('tools.pages.contentCalendarGenerator.importIcs')}
                                    </Button>
                                </>
                            )}
                        </div>

                        {generated && (
                            <p aria-live="polite" className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                {t('tools.pages.contentCalendarGenerator.slotsHint', { slots: slots.length, weeks: WEEKS })}
                            </p>
                        )}
                    </div>
                </section>

                {/* Result */}
                {generated && (
                    <section ref={resultRef} aria-label={t('tools.pages.contentCalendarGenerator.resultLabel')} className="pb-16 px-4 sm:px-6 lg:px-8 scroll-mt-28">
                        <div className="max-w-5xl mx-auto space-y-8">
                            {/* Pillar legend */}
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-6">
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                                    {t('tools.pages.contentCalendarGenerator.pillarLegend')}
                                </h2>
                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                    {Object.values(PILLARS).map((pillar) => (
                                        <div key={pillar.id} className="flex gap-3">
                                            <span
                                                className={`h-fit px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${pillar.accent}`}
                                            >
                                                {t(pillar.labelKey)}
                                            </span>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                                {t(pillar.purposeKey)}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Weeks */}
                            {Array.from({ length: WEEKS }, (_, week) => {
                                const weekSlots = slots.slice(
                                    week * postsPerWeek,
                                    (week + 1) * postsPerWeek
                                )
                                return (
                                    <div key={week}>
                                        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">
                                            {t('tools.pages.contentCalendarGenerator.week', { number: week + 1 })}
                                        </h3>
                                        {/* Phones get a stacked list: the 640px table made every row scroll sideways. */}
                                        <ul className="sm:hidden space-y-2">
                                            {weekSlots.map((slot, index) => (
                                                <li
                                                    key={`${toISODate(slot.date)}-${index}`}
                                                    className="p-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700"
                                                >
                                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">{formatDate(slot.date, i18n.language)}</span>
                                                        <span aria-hidden="true">·</span>
                                                        <span>{slot.platform}</span>
                                                        <span className={`ml-auto px-2 py-0.5 rounded-md font-medium ${PILLARS[slot.pillar].accent}`}>
                                                            {t(PILLARS[slot.pillar].labelKey)}
                                                        </span>
                                                    </div>
                                                    <p className="mt-2 text-sm text-gray-900 dark:text-white">{slot.idea}</p>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="hidden sm:block overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-700">
                                            <table className="w-full min-w-[640px] bg-white dark:bg-gray-800 text-sm">
                                                <thead>
                                                    <tr className="border-b border-gray-100 dark:border-gray-700 text-left">
                                                        <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 w-48">{t('tools.pages.contentCalendarGenerator.colDate')}</th>
                                                        <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 w-28">{t('tools.pages.contentCalendarGenerator.colPlatform')}</th>
                                                        <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400 w-32">{t('tools.pages.contentCalendarGenerator.colPillar')}</th>
                                                        <th className="px-4 py-3 font-medium text-gray-500 dark:text-gray-400">{t('tools.pages.contentCalendarGenerator.colIdea')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {weekSlots.map((slot, index) => (
                                                        <tr
                                                            key={`${toISODate(slot.date)}-${index}`}
                                                            className="border-b border-gray-50 dark:border-gray-700/50 last:border-0"
                                                        >
                                                            <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                                {formatDate(slot.date, i18n.language)}
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                                                                {slot.platform}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span
                                                                    className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${PILLARS[slot.pillar].accent}`}
                                                                >
                                                                    {t(PILLARS[slot.pillar].labelKey)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-900 dark:text-white">
                                                                {slot.idea}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </section>
                )}

                {/* Path to product */}
                <section className="pb-24 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-8 sm:p-10">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {t('tools.pages.contentCalendarGenerator.ctaHeading')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            {t('tools.pages.contentCalendarGenerator.ctaBody1')}
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {t('tools.pages.contentCalendarGenerator.ctaBody2')}
                        </p>
                        <Link to="/register" className={buttonClassName({ className: 'shadow-lg shadow-primary-500/50 group' })}>
                            {t('tools.pages.contentCalendarGenerator.ctaButton')}
                            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                    </div>

                    <ToolFAQSection heading={t('tools.pages.contentCalendarGenerator.faqHeading')} faqs={faqs} />
                </section>

                <MoreTools currentPath="/tools/content-calendar-generator" />
                <div className="pb-20" />
            </div>

            <LandingFooter />
        </div>
    )
}
